import { Box, Tooltip } from "@mui/material"
import { useTranslation } from "react-i18next"
import { visuallyHiddenSx } from "styles/common/typography"
import { IDashboardStudentClass } from "ts/interfaces/AdminDashboard"

import {
  formatCompactMoney,
  formatFullMoney,
  getClassLabel,
} from "./dashboardFormat"

export const Dash = ({ label }: { label: string }) => (
  <>
    <span aria-hidden="true">—</span>
    <Box component="span" sx={visuallyHiddenSx}>
      {label}
    </Box>
  </>
)

// The compact amount is for the eye; the exact one is in the tooltip and read out.
export const Amount = ({ value }: { value: number }) => (
  <>
    <Tooltip title={formatFullMoney(value)} describeChild>
      <span aria-hidden="true">{formatCompactMoney(value)}</span>
    </Tooltip>
    <Box component="span" sx={visuallyHiddenSx}>
      {formatFullMoney(value)}
    </Box>
  </>
)

// The first class, cut short so a long name can't widen the table, and "+N" for
// the rest; the full list is in the tooltip and read out. Focusable only when
// "+N" hides something, so the tooltip opens from the keyboard.
export const ClassCell = ({
  classes,
}: {
  classes: IDashboardStudentClass[]
}) => {
  const { t } = useTranslation()
  const label = getClassLabel(classes)
  if (!label) return <Dash label={t("adminDashboard.noClass")} />
  const tooltip = (
    <>
      {label.more > 0 && (
        <Box
          component="span"
          sx={{ display: "block", fontWeight: 600, mb: 0.5 }}
        >
          {t("adminDashboard.classCount", { count: label.names.length })}
        </Box>
      )}
      <Box
        component="ul"
        sx={{ m: 0, p: 0, listStyle: "none", lineHeight: 1.5 }}
      >
        {label.names.map((name, index) => (
          <li key={index}>{name}</li>
        ))}
      </Box>
    </>
  )
  return (
    <>
      <Tooltip title={tooltip} describeChild>
        <Box
          component="span"
          tabIndex={label.more > 0 ? 0 : undefined}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            borderRadius: "4px",
            "&:focus-visible": {
              outline: "2px solid",
              outlineColor: "mockup.primary60",
              outlineOffset: "2px",
            },
          }}
        >
          <Box
            component="span"
            aria-hidden="true"
            sx={{
              maxWidth: 96,
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {label.names[0]}
          </Box>
          {label.more > 0 && (
            <Box
              component="span"
              aria-hidden="true"
              sx={{
                ml: 0.75,
                px: 0.75,
                height: 20,
                display: "inline-flex",
                alignItems: "center",
                borderRadius: "10px",
                bgcolor: "mockup.neutral95",
                color: "mockup.neutral30",
                fontSize: "12px",
                fontWeight: 600,
                lineHeight: 1,
              }}
            >
              +{label.more}
            </Box>
          )}
        </Box>
      </Tooltip>
      <Box component="span" sx={visuallyHiddenSx}>
        {label.full}
      </Box>
    </>
  )
}
